//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { UnisonIconType } from '@bvengineering/unison-core';
import React from 'react';
import SVG from 'react-inlinesvg';
import styled from 'styled-components';

import {
  DEFAULT_SIZE,
  LG_SIZE,
  SM_SIZE,
} from '@components/atoms/icon/iconConstants';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

interface Props extends ITrackableComponent {
  className?: string;
  // Using iconSize rather than size to prevent conflicts with other components
  iconSize?: 'sm' | 'md' | 'lg';
  name: UnisonIconType;
}

/**
 * The Icon atom renders an SVG icon that will inherit the current text color of its container.
 *
 * The icon size can be set in two ways, with `iconSize` taking precedence:
 * - The `iconSize` prop: 'sm' (12px), 'md' (16px), or 'lg' (24px).
 * - A suffix on the `name` prop: '-sm' (12px) or '-lg' (24px).
 * If no size is specified, it defaults to 16px.
 *
 * The Icon can be styled directly via styled-components.
 */
export const Icon = ({ className, iconSize, name, trackingId }: Props) => {
  const size = (() => {
    if (name.includes('-sm') || iconSize === 'sm') {
      return SM_SIZE;
    } else if (name.includes('-lg') || iconSize === 'lg') {
      return LG_SIZE;
    } else {
      return DEFAULT_SIZE;
    }
  })();

  // We must use require syntax for a dynamic import so disable eslint to avoid errors.
  // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires
  const iconSrc: string = require(
    `@bvengineering/unison-core/lib/icons/${name}.svg`,
  ) as string;

  return (
    <SvgComponent
      aria-hidden={true}
      className={className}
      focusable={false}
      size={size}
      src={iconSrc}
      trackingId={trackingId}
    />
  );
};

const SvgComponent = withTracking(
  styled(SVG)<{ size: number }>(
    ({ size }) => `
    width: ${size}px;
    height: ${size}px;
    /** Sets the icon to the same color as the current font color */
    fill: currentColor;
    /** Ensures that icons don't unintentionally resize inside of flex containers */
    flex: none;
    /* Required for MS Edge to prevent clicks on SVG elements */
    pointer-events: none;
  `,
  ),
);

export default Icon;
